import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import EnhancedInlineEditor from './EnhancedInlineEditor';

interface InlineEditorManagerProps {
  isOpen: boolean;
  leadId: string;
  fieldConfig: any; // Full field configuration object
  currentValue: any;
  onSave: (leadId: string, fieldId: string, newValue: any) => Promise<boolean>;
  onClose: () => void;
  targetElement?: HTMLElement | null;
  initialPosition?: { top: number; left: number; width: number; height: number };
}

const InlineEditorManager: React.FC<InlineEditorManagerProps> = ({
  isOpen,
  leadId,
  fieldConfig,
  currentValue,
  onSave,
  onClose,
  targetElement,
  initialPosition
}) => {
  // Use pre-calculated position if available, otherwise calculate it
  const getInitialPosition = () => {
    if (initialPosition) {
      return initialPosition;
    }
    
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      
      return {
        top: rect.top + scrollY,
        left: rect.left + scrollX,
        width: rect.width,
        height: rect.height
      };
    } else {
      // Fallback position - center of screen
      return {
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 150,
        width: 300,
        height: 40
      };
    }
  };

  const [position] = useState(getInitialPosition); // No need for setPosition anymore

  const handleSave = async (newValue: any) => {
    try {
      const success = await onSave(leadId, fieldConfig.value, newValue);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving value:', error);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const editorElement = document.querySelector('[data-inline-editor="true"]');
    
    if (editorElement && !editorElement.contains(target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      });
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        });
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div data-inline-editor="true">
      <EnhancedInlineEditor
        value={currentValue}
        fieldConfig={fieldConfig}
        onSave={handleSave}
        onCancel={onClose}
        position={position}
      />
    </div>,
    document.body
  );
};

export default InlineEditorManager;